import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import SponsorshipOptionModel from "../../models/SponsorshipOptionModel";
import Loading from "../shared/Loading";
import SponsorshipForm from "./SponsorshipForm";
import { sanitizeHtml } from "../../helpers/htmlHelper";

const fetchSponsorshipOptions = async (): Promise<SponsorshipOptionModel[]> => {
    const response = await fetch("/api/sponsorship/options");
    if (!response.ok) {
        throw new Error("Failed to fetch sponsorship options");
    }
    return response.json();
};

const SponsorshipSignUp = () => {
    const [selectedOption, setSelectedOption] = useState<SponsorshipOptionModel | null>(null);

    const {
        data: options,
        isLoading,
        error,
    } = useQuery<SponsorshipOptionModel[], Error>({
        queryKey: ["sponsorshipOptions"],
        queryFn: fetchSponsorshipOptions,
    });

    if (isLoading) {
        return <Loading message="Loading Sponsorship Options..." />;
    }

    if (error) {
        return (
            <div className="text-center mt-5 text-danger">
                {error.message || "Failed to fetch sponsorship options"}
            </div>
        );
    }

    // If an option is selected, show the sign-up form
    if (selectedOption) {
        return (
            <SponsorshipForm
                option={selectedOption}
                onBack={() => setSelectedOption(null)}
            />
        );
    }

    // Show sponsorship option cards
    return (
        <div className="container pt-4">
            <div className="row align-items-center">
                <div className="col-8">
                    <h2 className="text-dark">Sponsor</h2>
                </div>
                <div className="col-4 d-flex justify-content-end">
                    <Link to="/sponsors" className="btn btn-primary d-flex align-items-center me-2">
                        <i className="bi bi-tag-fill me-2"></i> View Sponsors
                    </Link>
                    <Link to="/" className="btn btn-primary d-flex align-items-center">
                        <i className="bi bi-house me-2"></i> Home
                    </Link>
                </div>
                <div className="col-12">
                    <hr />
                </div>
            </div>

            <div className="row">
                <div className="col-12">
                    <div className="alert alert-info">
                        <p>Choose a sponsorship option below to support The sKRAMble!</p>
                        <p>Each sponsorship helps us celebrate the life of Mark Pulice Jr. and support the memorial scholarship fund.</p>
                    </div>
                </div>
            </div>

            {(!options || options.length === 0) && (
                <div className="alert alert-warning">
                    No sponsorship options are currently available. Please check back later.
                </div>
            )}

            <div className="row">
                {options?.map((option) => (
                    <div key={option.sponsorshipOptionId} className="col-lg-4 col-md-6 col-sm-12 mb-3">
                        <div
                            className={`card h-100 ${option.isSoldOut ? "border-secondary" : "border-primary"}`}
                            style={{
                                opacity: option.isSoldOut ? 0.6 : 1,
                                cursor: option.isSoldOut ? "default" : "pointer",
                                transition: "transform 0.2s ease-in-out",
                            }}
                            onClick={() => !option.isSoldOut && setSelectedOption(option)}
                            onMouseEnter={(e) => {
                                if (!option.isSoldOut) {
                                    (e.currentTarget as HTMLElement).style.transform = "scale(1.03)";
                                }
                            }}
                            onMouseLeave={(e) => {
                                (e.currentTarget as HTMLElement).style.transform = "scale(1)";
                            }}
                        >
                            <div className="card-body d-flex flex-column text-center">
                                <h4 className="card-title">{option.title}</h4>
                                <h2 className="text-primary my-3">${option.amount.toFixed(2)}</h2>
                                {option.description && (
                                    <div className="card-text text-muted flex-grow-1 rich-text-content" dangerouslySetInnerHTML={{ __html: sanitizeHtml(option.description) }} />
                                )}
                                <div className="mt-auto">
                                    {option.isSoldOut ? (
                                        <span className="badge bg-danger fs-6 px-3 py-2">Sold Out</span>
                                    ) : (
                                        <>
                                            <p className="text-muted small mb-2">
                                                {option.limit === 0
                                                    ? "Unlimited availability"
                                                    : `${option.limit - option.claimedCount} of ${option.limit} remaining`}
                                            </p>
                                            <button className="btn btn-primary w-100">
                                                Select
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default SponsorshipSignUp;
